import { datadogRum } from '@datadog/browser-rum';
import { css } from '@emotion/react';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Heading } from '@spark-web/heading';
import { Inline } from '@spark-web/inline';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLinkButton } from '@spark-web/text-link';
import { useTheme } from '@spark-web/theme';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';

import PromoTag from '~/components/vendors/promo-tag';
import { useModal } from '~/hooks/useModal';
import { usePromotionContents } from '~/hooks/usePromotionContents';

type PromoBannerProps = {
  code: string;
  channel: string | null;
};

const PromoBanner = ({ code, channel }: PromoBannerProps) => {
  const theme = useTheme();

  const { open } = useModal('promotion-information');

  const [promotions, setPromotions] = useQueryState('promotions', parseAsArrayOf(parseAsString).withDefault([]));

  const { data } = usePromotionContents({ promotionCodes: [code], channel });

  const onCtaButtonClick = () => {
    datadogRum.addAction('Explore Vendors With Promotions Clicked', {
      promotionCode: code,
      channel: channel ?? '',
    });
    if (!promotions.includes(code)) {
      setPromotions((prev) => [...prev, code], { history: 'push', scroll: true });
    }
  };

  return (
    <Box
      background="neutral"
      data={{ testId: 'promotion-banner' }}
      css={css({
        [`@media (min-width: ${theme.breakpoint.tablet}px)`]: {
          gridColumn: 'span 2',
        },
      })}
    >
      <Stack>
        <img
          src={data?.imageBanner.url}
          alt={`${data?.title} banner`}
          width={data?.imageBanner.width}
          height={data?.imageBanner.height}
          style={{ objectFit: 'cover', width: '100%', height: 'auto' }}
        />
      </Stack>

      <Stack paddingX="large" paddingY="xxlarge" gap="xlarge" align="left">
        <PromoTag theme="pantheon">
          <Text size="xsmall">Battery offer</Text>
        </PromoTag>
        <Heading as="h3" level="2">
          {data?.heading}
        </Heading>
        <Inline gap="xsmall">
          <Text>{data?.summary}—</Text>
          <Text tone="info">
            <TextLinkButton onClick={open}>See details</TextLinkButton>
          </Text>
        </Inline>
        <Button prominence="low" tone="dark" rounded onClick={onCtaButtonClick}>
          Explore vendors
        </Button>
      </Stack>
    </Box>
  );
};

export { PromoBanner };
